import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { PlatformEnum, SelectedCustomerState } from '@panamax/app-state';
import { Customer } from '@usf/customer-types';
import { AccountSummaryState } from '@usf/ngrx-payment';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerSelectionPaymentsTouchComponent } from 'src/app/shared/components/header/customer-selection/customer-selection-payments-touch/customer-selection-payments-touch.component';
import { CustomerSelectionOption } from '../../../customer/models/customer-selection-option.model';
import { CustomerSelectionVM } from '../../../customer/models/customer-selection-vm.model';
import { ModalService } from '../../../shared/services/modal/modal.service';

@Component({
  selector: 'app-account-balance',
  templateUrl: './account-balance.component.html',
  styleUrls: ['./account-balance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountBalanceComponent implements OnChanges {
  @Input() accountSummaryState: AccountSummaryState;
  @Input() enablePayBalanceButton = false;
  @Input() platformEnum: PlatformEnum;
  @Input() isTouch: boolean;
  @Input() customers: Customer[];
  @Input() selectedCustomer: SelectedCustomerState[];
  @Input() showAllCustomersOption: boolean;
  @Input() showMakePayments: boolean;
  @Input() isSuperUser: boolean;

  @Output() customerSwitchEmitter = new EventEmitter<Customer>();
  @Output() allInvoicesClickEmitter = new EventEmitter<any>();
  @Output() payAccountBalanceClickEmitter = new EventEmitter<any>();

  searchText$ = new BehaviorSubject<string>('');
  customerSelectorLabel: string;

  constructor(
    protected popoverController: PopoverController,
    protected modalController: ModalController,
    protected modalService: ModalService,
  ) {}

  ngOnChanges() {
    this.customerSelectorLabel = this.getCustomerSelectorLabel();
  }

  private getCustomerSelectorLabel(): string {
    const selectedCustomers = this.selectedCustomer.length;

    if (selectedCustomers === 1) {
      return this.selectedCustomer[0].customerName;
    }
    if (
      selectedCustomers === this.customers.length &&
      this.showAllCustomersOption
    ) {
      return 'All Customers';
    }

    return 'Multiple Customers';
  }

  async openCustomerSelectionPopover(ev: any) {
    let customerSelectionElement: HTMLIonModalElement | HTMLIonPopoverElement;
    const props = {
      searchText$: this.searchText$,
      vm$: this.getCustomerSelectionVM$(),
    };

    if (this.isTouch) {
      // Mobile Customer Selector
      this.modalService.setModalOptions(
        true,
        CustomerSelectionPaymentsTouchComponent,
        props,
      );
      customerSelectionElement = await this.modalController.create(
        this.modalService.modalOptions,
      );
    } else {
      //Desktop Customer Selector
      this.modalService.setModalOptions(
        false,
        CustomerSelectionPaymentsTouchComponent,
        props,
        'select-customers-desktop-modal',
      );
      customerSelectionElement = await this.modalController.create(
        this.modalService.modalOptions,
      );
    }

    await customerSelectionElement.present();

    await customerSelectionElement.onDidDismiss().then((resp: any) => {
      if (resp?.data) {
        this.customerSwitchEmitter.emit(resp.data.customers);
      }
      this.searchText$.next('');
    });
  }

  goToAllInvoices() {
    this.allInvoicesClickEmitter.emit();
  }

  private getCustomerSelectionVM$() {
    return combineLatest([
      of(this.customers),
      of(this.selectedCustomer),
      this.searchText$,
    ]).pipe(
      map(([customers, selectedCustomerState, searchText]) =>
        this.constructCustomerSelectionVM(
          customers,
          searchText,
          selectedCustomerState,
        ),
      ),
    );
  }

  private constructCustomerSelectionVM(
    customers: Customer[],
    searchText: string,
    selectedCustomerState: SelectedCustomerState[],
  ) {
    const isDesktop = this.platformEnum === PlatformEnum.desktop;
    const customerRowHeight = isDesktop ? 54 : 68;
    const customerSelectionOptions: CustomerSelectionOption[] = [];
    const rowHeights: number[] = [];

    customers
      .filter(
        customer =>
          customer.customerName
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          customer.customerNumber.toString().includes(searchText.toLowerCase()),
      )
      .forEach(customer => {
        const title =
          customer.customerName +
          (customer.customerNumber === 0
            ? ''
            : ' (' + customer.customerNumber + ')');
        const alphabets = title.match('[a-zA-Z]{1}');
        let customerInfo: CustomerSelectionOption = {
          title,
          subtitle: '',
          firstLetter: !!alphabets?.length ? alphabets[0] : '',
          customerName: customer.customerName,
          customerNumber: customer.customerNumber,
          departmentNumber: '0',
          hasDepartments: customer.departments?.length ? true : false,
          isDepartment: false,
          isSelected:
            !!selectedCustomerState.find(
              c => c.customerNumber === customer?.customerNumber,
            ) || customer.customerNumber === 0,
        };
        customerSelectionOptions.push(customerInfo);
        rowHeights.push(customerRowHeight);
      });
    //By default all customers is selected, if any of the customers is not selected it resets the flag to false
    if (
      customerSelectionOptions.find(c => c.customerNumber === 0) &&
      customerSelectionOptions.find(c => c.isSelected === false)
    ) {
      customerSelectionOptions[
        customerSelectionOptions.findIndex(c => c.customerNumber === 0)
      ].isSelected = false;
    }
    const vm: CustomerSelectionVM = {
      customerSelectionOptions,
      rowHeights,
      platformType: this.platformEnum,
      showAllCustomersOption: this.showAllCustomersOption,
    };
    return vm;
  }

  onPayBalanceClick() {
    this.payAccountBalanceClickEmitter.emit();
  }
}
